import User from "./User";
import { ImageMetaData } from "./Photo";

export const PRIVATE_MISSION_ID_SEARCH_LENGTH = 6;

export type UserMissionData = {
  uid: string;
  pieces: number;
  displayName: string;
};
export type PendingUser = { uid: string; displayName: string; email: string };
export type TotalUserPieces = { [uid: string]: UserMissionData };
export type PendingUsers = Array<PendingUser>;

export type MissionFirestoreData = Omit<
  ConfigurableMissionData,
  "coverPhoto"
> & {
  id: string;
  ownerUserId: string;
  totalPieces: number;
  totalUserPieces: TotalUserPieces;
  pendingUsers: PendingUsers;
  hidden: boolean;
};

export type Mission = MissionFirestoreData & {
  coverPhotoUrl?: any;
};

export type ConfigurableMissionData = {
  name: string;
  description: string;
  isPrivate: boolean;
  startTime: number;
  endTime: number;
  targetPieces: number;
  coverPhoto?: ImageMetaData | string;
  precedence?: number;
};

export const equal = (
  mission: ConfigurableMissionData,
  other: ConfigurableMissionData
) => {
  return (
    mission.name === other.name &&
    mission.description === other.description &&
    mission.targetPieces === other.targetPieces &&
    mission.isPrivate === other.isPrivate &&
    isSameDay(new Date(mission.startTime), new Date(other.startTime)) &&
    isSameDay(new Date(mission.endTime), new Date(other.endTime))
  );
};

export const EmptyMissionData: ConfigurableMissionData = {
  name: "",
  description: "",
  isPrivate: false,
  startTime: 0,
  endTime: 0,
  targetPieces: 0,
  coverPhoto: undefined,
  precedence: 0
};

export const missionHasEnded = (endTime: number): boolean => {
  const today: Date = new Date();
  today.setHours(0, 0, 0, 0);
  return endTime < today.getTime();
};

export const missionHasStarted = (mission: {
  startTime: MissionFirestoreData["startTime"];
}): boolean => {
  const today: Date = new Date();
  today.setHours(0, 0, 0, 0);
  return mission.startTime < today.getTime();
};

export const missionIsCompleted = (mission: {
  totalPieces: MissionFirestoreData["totalPieces"];
  targetPieces: MissionFirestoreData["targetPieces"];
}): boolean => {
  return mission.totalPieces >= mission.targetPieces;
};

export const isSameDay = (date: Date, other: Date) => {
  return date.toDateString() === other.toDateString();
};

export function isDuplicatingExistingMissionName(
  missionConfigurableData: ConfigurableMissionData | undefined,
  existingMissions: MissionFirestoreData[],
  currentMissionId?: string
) {
  if (missionConfigurableData === undefined) {
    return false;
  }

  const existingMissionHasName = existingMissions.some(
    (existingMission: MissionFirestoreData) => {
      return (
        existingMission.name === missionConfigurableData.name &&
        existingMission.id !== currentMissionId
      );
    }
  );

  return existingMissionHasName;
}

export function isMissionDataValid(
  missionConfigurableData: ConfigurableMissionData | undefined
) {
  const today: Date = new Date();
  today.setHours(0, 0, 0, 0);
  return (
    missionConfigurableData !== undefined &&
    // Chehck mission has a name
    missionConfigurableData.name !== "" &&
    // Check mission has description
    missionConfigurableData.description !== "" &&
    // Has a valid number of pieces to collect
    missionConfigurableData.targetPieces > 0 &&
    // Has an end date set after the start date
    missionConfigurableData.endTime > missionConfigurableData.startTime
  );
}

export const userIsInMission = (
  user: User | undefined | null,
  missionId: string
): boolean => {
  return (
    user !== null && user !== undefined && user?.missions.includes(missionId)
  );
};

export const userOnMissionLeaderboard = (
  mission: MissionFirestoreData,
  userId: string
): boolean => {
  return userId in mission.totalUserPieces;
};

export const userCollectedPiecesForMission = (
  mission: MissionFirestoreData,
  userId: string
): boolean => {
  return (
    userOnMissionLeaderboard(mission, userId) &&
    mission.totalUserPieces[userId].pieces > 0
  );
};

export const userIsInPendingMissionMembers = (
  mission: MissionFirestoreData,
  userId: string
): boolean => {
  return mission.pendingUsers.some((user) => user.uid === userId);
};

export const getDaysBetweenTimes = (
  startTimeMs: number,
  endTimeMs: number
): number => {
  return Math.floor((endTimeMs - startTimeMs) / (1000 * 60 * 60 * 24)) + 1;
};
